import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import {
    updateNotRevealedContactsIds,
    updateRevealedContactsById,
} from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import { useContactsFlowStrategy } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import { updateUsedContactsQuota } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import type { ContactSearchEntry } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { RevealedContactDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { selectImpersonateMode } from "app/selectors/impersonation";

export interface IRevealedContactResponse {
    revealedInfo: RevealedContactDto;
    isRevealed: boolean;
}

const useSingleRevealContactInfoQuery = (
    onSuccess?: (data: IRevealedContactResponse, contactEntry: ContactSearchEntry) => void,
): UseMutationResult<IRevealedContactResponse> => {
    const dispatch = useDispatch();
    const strategy = useContactsFlowStrategy();

    const impersonateMode: boolean = useSelector(selectImpersonateMode);

    const transformSingleContactRevealData = (data: RevealedContactDto): IRevealedContactResponse => {
        return { revealedInfo: data, isRevealed: true };
    };

    return useMutation({
        retry: false,
        throwOnError: true,
        mutationFn: async (contactEntry: ContactSearchEntry): Promise<IRevealedContactResponse> => {
            const {
                id,
                companyId,
                originalSite,
                hasValidEmails,
                hasValidEmailsSuppressed,
                hasDirectPhoneNumbers,
                hasDirectPhoneNumbersSuppressed,
                hasMobilePhoneNumbers,
                hasMobilePhoneNumbersSuppressed,
            } = contactEntry;

            const data = await strategy.dataSource.revealSingle({
                contactId: id,
                companyId,
                prospect: originalSite,
                hasValidEmails,
                hasValidEmailsSuppressed,
                hasDirectPhoneNumbers,
                hasDirectPhoneNumbersSuppressed,
                hasMobilePhoneNumbers,
                hasMobilePhoneNumbersSuppressed,
            });

            return transformSingleContactRevealData(data);
        },
        onSuccess: (data: IRevealedContactResponse, contactEntry: ContactSearchEntry) => {
            trackingService.trackContactEntrySingleRevealSuccess();

            if (data.revealedInfo == null) {
                dispatch(updateNotRevealedContactsIds([contactEntry.id]));
                return;
            }

            const revealErrors = data.revealedInfo?.revealErrors;
            const hasRevealErrors = Boolean(
                revealErrors?.hasValidEmailsError ||
                    revealErrors?.hasDirectPhoneNumbersError ||
                    revealErrors?.hasMobilePhoneNumbersError,
            );

            if (!contactEntry.isRevealed && !impersonateMode && !hasRevealErrors) {
                dispatch(updateUsedContactsQuota(1));
            }

            if (data.revealedInfo) {
                dispatch(updateRevealedContactsById([data.revealedInfo]));
            }

            onSuccess?.(data, contactEntry);
        },
        onError: () => {
            trackingService.trackContactEntrySingleRevealFailure();
        },
    });
};

export default useSingleRevealContactInfoQuery;
